 

import { CONTENT_TYPE } from 'bricks/constants';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import { URLS } from 'services/urls';
import { ITotalRentalIncome, TotalGrossRent } from 'views/ProFormaTable/types';
import { CollapsibleCard, InvestmentMemoFormData, FieldPath } from './types';

export const AI_ICON_URL = URLS.IMAGE_CLOUDFRONT_URL('ns_ai_with_stars.png');

export const initialInvestmentMemoFormData: InvestmentMemoFormData = {
    title: {
        dealName: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        sponsor: '',
        date: '',
        tagline: '',
        logoFiles: [],
        buildingExteriorFiles: {
            uploaded: [],
            suggested: [],
        },
    },
    executiveSummary: {
        opportunityDescription: '',
        totalCapitalization: '',
        projectedLpIrr: '',
        avgCashOnCash: '',
        holdPeriod: '',
        totalEquityRequired: '',
        projectedEquityMultiple: '',
        buildPeriod: '',
    },
    investmentHighlights: {
        highlights: [],
    },
    location: {
        coordinates: DEFAULT_NYC_COORDINATES,
        drivers: [],
    },
    dealOverview: {
        siteStackingPlanFiles: {
            uploaded: [],
            suggested: [],
        },
        specs: {
            siteArea: 0,
            buildingSizeGrossSF: 0,
            buildingSizeNetRentableSF: 0,
            parkingSpaces: {
                structured: null,
                surface: null,
                underground: null,
                trailer: null,
            },
            proFormaModelId: undefined,
            totalUnits: 0,
        },
        unitMixRentRoll: {
            data: {
                type: 'rent-roll',
                value: [],
                totals: {
                    totalPricePerSf: 0,
                    totalPricePerUnit: 0,
                    totalAmountPerYear: 0,
                },
                isDataCenter: false,
            },
        },
    },
    team: {
        sponsorCompanyName: '',
        sponsorBio: '',
        assetsUnderManagement: '',
        principals: [],
        vendors: {},
    },
    marketOverview: {
        fundamentals: [],
    },
    developmentTimeline: {
        milestones: [],
    },
    financials: {
        financialSummary: {
            includeAssumptions: true,
            includeForecast: true,
            includeOperations: true,
            includeReturns: true,
        },
        development: null,
        assumptions: null,
        forecast: null,
        operations: null,
        returns: null,
    },
};

export const SUMMARY_FORM_NAMES = {
    TITLE: 'title',
    EXECUTIVE_SUMMARY: 'executive-summary',
    INVESTMENT_HIGHLIGHTS: 'investment-highlights',
} as const;

export const SUMMARY_FORMS: CollapsibleCard[] = [
    {
        id: SUMMARY_FORM_NAMES.TITLE,
        number: 1,
        title: 'Title',
        subtitle: 'General information, location, positioning; pre-populated fields are auto-filled from pro forma',
    },
    {
        id: SUMMARY_FORM_NAMES.EXECUTIVE_SUMMARY,
        number: 2,
        title: 'Executive Summary',
        subtitle: 'Key metrics and investment opportunity overview',
    },
    {
        id: SUMMARY_FORM_NAMES.INVESTMENT_HIGHLIGHTS,
        number: 3,
        title: 'Investment Highlights',
        subtitle: 'Auto-filled descriptions are generated using our internal AI-powered tool',
    },
];

export const DEAL_OVERVIEW_FORM_NAMES = {
    DEAL_OVERVIEW: 'deal-overview',
    LOCATION: 'location',
    DEVELOPMENT_TIMELINE: 'development-timeline',
    TEAM: 'team',
    MARKET_OVERVIEW: 'market-overview',
} as const;

export const DEAL_OVERVIEW_FORMS: CollapsibleCard[] = [
    {
        id: DEAL_OVERVIEW_FORM_NAMES.DEAL_OVERVIEW,
        number: 4,
        title: 'Deal overview',
        subtitle: 'Property specifications and unit mix',
    },
    {
        id: DEAL_OVERVIEW_FORM_NAMES.LOCATION,
        number: 5,
        title: 'Location',
        subtitle: 'Auto-filled descriptions are generated using our internal AI-powered tool',
    },
    {
        id: DEAL_OVERVIEW_FORM_NAMES.DEVELOPMENT_TIMELINE,
        number: 6,
        title: 'Development timeline',
        subtitle: 'Key project milestones',
    },
    {
        id: DEAL_OVERVIEW_FORM_NAMES.TEAM,
        number: 7,
        title: 'Team',
        subtitle: 'Auto-filled descriptions are generated using our internal AI-powered tool',
    },
    {
        id: DEAL_OVERVIEW_FORM_NAMES.MARKET_OVERVIEW,
        number: 8,
        title: 'Market overview',
        subtitle: 'Auto-filled descriptions are generated using our internal AI-powered tool',
    },
];

export const FINANCIALS_FORM_NAMES = {
    SOURCES_USES: 'sources-uses',
    FINANCIAL_SUMMARY: 'financial-summary',
} as const;

export const FINANCIALS_FORMS: CollapsibleCard[] = [
    {
        id: FINANCIALS_FORM_NAMES.SOURCES_USES,
        number: 9,
        title: 'Sources & uses',
        subtitle: 'Capital structure populated from pro forma',
    },
    {
        id: FINANCIALS_FORM_NAMES.FINANCIAL_SUMMARY,
        number: 10,
        title: 'Financial summary',
        subtitle: 'Pro forma projections',
    },
];

export const FILE_UPLOAD_CONFIG = {
    ACCEPTED_IMAGE_TYPES: [
        CONTENT_TYPE.PNG,
        CONTENT_TYPE.JPG,
        CONTENT_TYPE.JPEG,
        CONTENT_TYPE.SVG,
    ].join(', '),
    MAX_FILE_SIZE_BYTES: 2 * 1024 * 1024, // 2MB
    MAX_FILE_SIZE_LABEL: '2MB',
} as const;

export const MEMO_SECTIONS = [
    {
        TITLE: 'Summary',
        DESCRIPTION: 'Input key information for the opening slides of your investment memo deck',
        TOTAL_KEY_FIELDS: 3,
    },
    {
        TITLE: 'Deal Overview',
        DESCRIPTION: 'Input details, location, timeline, and team information',
        TOTAL_KEY_FIELDS: 5,
    },
    {
        TITLE: 'Financials',
        DESCRIPTION: 'Input key information for the opening slides of your investment memo deck',
        TOTAL_KEY_FIELDS: 2,
    },
] as const;

export enum InvestmentMemoNavItem {
    Summary = 'summary',
    DealOverview = 'deal-overview',
    Financials = 'financials',
}

export const INVESTMENT_MEMO_NAV = {
    TITLE: 'Investment memo deck builder',
    SUBTITLE: 'Create a professional offering deck for your real estate development deal.',
    NAV_ITEMS: {
        [InvestmentMemoNavItem.Summary]: 'Summary',
        [InvestmentMemoNavItem.DealOverview]: 'Deal Overview',
        [InvestmentMemoNavItem.Financials]: 'Financials',
    },
};

export const TEAM_VIEW_ENTITY = {
    MANUALLY: 'manually',
    CONTACTS: 'contacts',
} as const;

export const TEAM_VENDORS = [
    { key: 'legal', label: 'Legal' },
    { key: 'architect', label: 'Architect' },
    { key: 'engineer', label: 'Engineer' },
    { key: 'surveyor', label: 'Surveyor' },
    { key: 'environmentalConsultant', label: 'Environmental Consultant' },
];

export const DEFAULT_GROSS_RENT_TOTALS: TotalGrossRent = {
    totalUnits: 0,
    rentableSf: 0,
    averageSf: 0,
    pricePerSf: 0,
    pricePerUnit: 0,
    totalAmountPerYear: 0,
};

export const DEFAULT_RENTAL_INCOME_TOTALS: ITotalRentalIncome = {
    totalPricePerSf: 0,
    totalPricePerUnit: 0,
    totalAmountPerYear: 0,
};

export const MONTHS_IN_YEAR = 12;

export const EXPORT_FORMAT = {
    PDF: 'pdf',
    PPTX: 'pptx',
} as const;

export const CARD_REQUIRED_FIELDS: Record<string, FieldPath[]> = {
    [SUMMARY_FORM_NAMES.TITLE]: [
        'title.dealName', 'title.address', 'title.city', 'title.state',
        'title.zipCode', 'title.sponsor', 'title.date',
    ],
    [SUMMARY_FORM_NAMES.EXECUTIVE_SUMMARY]: [
        'executiveSummary.opportunityDescription', 'executiveSummary.totalCapitalization',
        'executiveSummary.projectedLpIrr', 'executiveSummary.avgCashOnCash',
        'executiveSummary.holdPeriod', 'executiveSummary.totalEquityRequired',
        'executiveSummary.projectedEquityMultiple', 'executiveSummary.buildPeriod',
    ],
    [SUMMARY_FORM_NAMES.INVESTMENT_HIGHLIGHTS]: [
        'investmentHighlights.highlights',
    ],
    [DEAL_OVERVIEW_FORM_NAMES.DEAL_OVERVIEW]: [
        'dealOverview.specs.assetType', 'dealOverview.specs.assetSubtype',
        'dealOverview.specs.siteArea', 'dealOverview.specs.buildingSizeGrossSF',
        'dealOverview.specs.buildingSizeNetRentableSF',
    ],
    [DEAL_OVERVIEW_FORM_NAMES.LOCATION]: [
        'location.coordinates', 'location.drivers',
    ],
    [DEAL_OVERVIEW_FORM_NAMES.DEVELOPMENT_TIMELINE]: [
        'developmentTimeline.milestones',
    ],
    [DEAL_OVERVIEW_FORM_NAMES.TEAM]: [
        'team.sponsorCompanyName',
    ],
    [DEAL_OVERVIEW_FORM_NAMES.MARKET_OVERVIEW]: [],
};

export const NAV_ITEM_CARDS: Record<InvestmentMemoNavItem, string[]> = {
    [InvestmentMemoNavItem.Summary]: [
        SUMMARY_FORM_NAMES.TITLE,
        SUMMARY_FORM_NAMES.EXECUTIVE_SUMMARY,
        SUMMARY_FORM_NAMES.INVESTMENT_HIGHLIGHTS,
    ],
    [InvestmentMemoNavItem.DealOverview]: [
        DEAL_OVERVIEW_FORM_NAMES.DEAL_OVERVIEW,
        DEAL_OVERVIEW_FORM_NAMES.LOCATION,
        DEAL_OVERVIEW_FORM_NAMES.DEVELOPMENT_TIMELINE,
        DEAL_OVERVIEW_FORM_NAMES.TEAM,
        DEAL_OVERVIEW_FORM_NAMES.MARKET_OVERVIEW,
    ],
    [InvestmentMemoNavItem.Financials]: [
        FINANCIALS_FORM_NAMES.SOURCES_USES,
        FINANCIALS_FORM_NAMES.FINANCIAL_SUMMARY,
    ],
};
